import { Button } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useState } from 'react';

const useStyles = makeStyles(() => ({
    card: {
        backgroundColor: '#FFFFFF',
        border: '1.5px solid #E3E3FA',
        borderRadius: '8px',
        padding: '24px',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
    },
    title: {
        fontFamily: 'Manrope',
        fontSize: '16px',
        fontWeight: 600,
        color: '#1F2152'
    },
    closeButton: {
        minWidth: 'unset',
        padding: '8px',
        color: '#7378E8'
    },
    infoSection: {
        marginBottom: '24px'
    },
    infoLabel: {
        fontFamily: 'Manrope',
        fontSize: '14px',
        color: '#787880',
        marginBottom: '8px',
        display: 'block'
    },
    content: {
        padding: '16px',
        backgroundColor: '#F6F7FF',
        borderRadius: '8px',
        marginBottom: '16px'
    },
    link: {
        fontFamily: 'Manrope',
        fontSize: '14px',
        color: '#1F2152',
        wordBreak: 'break-all'
    },
    buttonContainer: {
        display: 'flex',
        gap: '16px'
    },
    button: {
        flex: 1,
        padding: '12px',
        backgroundColor: '#FFFFFF',
        color: '#7378E8',
        border: '1.5px solid #7378E8',
        borderRadius: '8px',
        '&:hover': {
            backgroundColor: '#F6F7FF'
        }
    }
}));

type Props = {
    onClose: () => void;
    authLink: string | null;
    sendEmail: () => Promise<{ success: boolean; error?: string }>;
    canSendEmail: boolean;
};

const AuthLinkCard: React.FC<Props> = ({ onClose, authLink, sendEmail, canSendEmail }) => {
    const classes = useStyles();
    const [copyFeedback, setCopyFeedback] = useState(false);
    const [sendingEmail, setSendingEmail] = useState(false);

    const handleCopyLink = async () => {
        if (authLink) {
            try {
                await navigator.clipboard.writeText(authLink);
                setCopyFeedback(true);
                setTimeout(() => setCopyFeedback(false), 2000);
            } catch (error) {
                console.error('Failed to copy to clipboard:', error);
            }
        }
    };

    const handleSendEmail = async () => {
        setSendingEmail(true);
        try {
            const result = await sendEmail();
            if (result.success) {
                // Could add success feedback here
            } else {
                console.error('Failed to send email:', result.error);
            }
        } catch (error) {
            console.error('Error sending email:', error);
        } finally {
            setSendingEmail(false);
        }
    };

    return (
        <div className={classes.card}>
            <div className={classes.header}>
                <span className={classes.title}>Member Portal Auth Link</span>
                <Button 
                    className={classes.closeButton}
                    onClick={onClose}
                >
                    <i className="far fa-times" />
                </Button>
            </div>
            <div className={classes.infoSection}>
                <div className={classes.content}>
                    <span className={classes.link}>
                        {authLink || 'No link available'}
                    </span>
                </div>
            </div>
            <div className={classes.buttonContainer}>
                <Button 
                    className={classes.button}
                    onClick={handleCopyLink}
                    disabled={!authLink}
                >
                    <i className="far fa-copy" style={{ marginRight: '12px' }} />
                    {copyFeedback ? 'Copied to clipboard!' : 'Copy Link'}
                </Button>
                {canSendEmail && (
                    <Button 
                        className={classes.button}
                        onClick={handleSendEmail}
                        disabled={sendingEmail}
                    >
                        <i className="far fa-share" style={{ marginRight: '12px' }} />
                        {sendingEmail ? 'Sending...' : 'Send Email/Text'}
                    </Button>
                )}
            </div>
        </div>
    );
};

export default AuthLinkCard; 